import {
  useState,
  useEffect,
  useMemo,
  useRef,
  ForwardRefRenderFunction,
  forwardRef
} from 'react'
import { Checkbox, Label, FilterTag, Icon } from 'components'
import useOnClickOutside from 'commons/customHooks/useOnClickOutside'

import * as S from './styles'

export type MultiselectProps = {
  width?: number
  height?: number
  label?: string
  inputPlaceholder?: string
  optionsList: string[]
  selectedOptions: string[]
  onChange: (options: string[]) => void
  error?: boolean
  disabled?: boolean
}

const MultiselectComponent: ForwardRefRenderFunction<
  HTMLSelectElement,
  MultiselectProps
> = (
  {
    width,
    height = 30,
    label,
    inputPlaceholder = 'Selecionar',
    optionsList,
    selectedOptions,
    onChange,
    error = false,
    disabled = false
  },
  ref
) => {
  const [open, setOpen] = useState(false)
  const [searchValue, setSearchValue] = useState('')

  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const fieldRef = useRef<HTMLDivElement>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)
  useOnClickOutside(fieldRef, () => setOpen(false))

  const allOptionsSelected = () => selectedOptions.length === optionsList.length

  const handleSelectAll = () => {
    if (allOptionsSelected()) {
      onChange([])
    } else {
      onChange(optionsList)
    }
  }

  const handleRemove = (option: string) => {
    onChange(selectedOptions.filter((selected) => selected !== option))
  }

  const handleSelectSingle = (option: string) => {
    if (selectedOptions.includes(option)) {
      onChange(selectedOptions.filter((item) => item !== option))
    } else {
      onChange([...selectedOptions, option])
    }
    inputRef.current?.focus()
  }

  const isOptionSelected = (option: string) => {
    return selectedOptions.includes(option)
  }

  const toggleDropdownVisibility = () => {
    setOpen((prev) => !prev)
  }

  const options = useMemo(() => {
    if (searchValue === '') return optionsList
    return optionsList.filter((opt) =>
      opt.toLocaleLowerCase().includes(searchValue.toLocaleLowerCase())
    )
  }, [searchValue, optionsList])

  useEffect(() => {
    if (open) inputRef.current?.focus()
    else {
      setSearchValue('')
      if (containerRef.current?.scrollTo) containerRef.current?.scrollTo(0, 0)
    }
  }, [open])

  return (
    <S.MultiselectContainer width={width} ref={wrapperRef}>
      {label && (
        <S.LabelWrapper disabled={disabled}>
          <Label id="multiselect-label">{label}</Label>
        </S.LabelWrapper>
      )}
      <S.FieldWrapper
        ref={fieldRef || ref}
        error={error}
        height={height}
        disabled={disabled}
        data-testid="field-wrapper"
      >
        <S.FieldContainer ref={containerRef} width={wrapperRef.current?.clientWidth}>
          {allOptionsSelected() ? (
            <FilterTag
              key={`filter-tag-all-selected`}
              content="Todos"
              disabled={disabled}
            />
          ) : (
            selectedOptions.map((option) => (
              <FilterTag
                key={`filter-tag-${option}`}
                content={option}
                disabled={disabled}
                onRemove={() => handleRemove(option)}
              />
            ))
          )}
          <S.SearchInput
            ref={inputRef}
            placeholder={!selectedOptions.length ? inputPlaceholder : ''}
            value={searchValue}
            height={height}
            onClick={() => setOpen(true)}
            onChange={(e) => setSearchValue(e.target.value)}
            data-testid="multiselect-input"
            disabled={disabled}
          />
        </S.FieldContainer>

        <S.DropdownButton
          type="button"
          disabled={disabled}
          height={height}
          onClick={toggleDropdownVisibility}
          open={open}
          data-testid="dropdown-button"
        >
          <Icon icon="chevron-up" size="20" />
        </S.DropdownButton>
        {open && (
          <S.DropdownContainer height={height}>
            <S.OptionsWrapper data-testid="multiselect-options">
              {!searchValue && (
                <S.OptionRow key={`multiselect-option-selectall`}>
                  <Checkbox checked={allOptionsSelected()} onChange={handleSelectAll} />
                  <S.OptionText>Selecionar todos</S.OptionText>
                </S.OptionRow>
              )}
              {options.map((option) => (
                <S.OptionRow
                  key={`multiselect-option-${option}`}
                  data-testid={`option-row-${option}`}
                >
                  <Checkbox
                    checked={isOptionSelected(option)}
                    onChange={() => handleSelectSingle(option)}
                  />
                  <S.OptionText>{option}</S.OptionText>
                </S.OptionRow>
              ))}
              {!options.length && (
                <S.OptionRow key={`multiselect-option-not-found`}>
                  <S.OptionText>Nenhuma opção foi encontrada na busca.</S.OptionText>
                </S.OptionRow>
              )}
            </S.OptionsWrapper>
          </S.DropdownContainer>
        )}
      </S.FieldWrapper>
    </S.MultiselectContainer>
  )
}

export const Multiselect = forwardRef(MultiselectComponent)
